import validate from 'lib/utils/validationRules';

import router from '@/router';

import HTTPService from '@/services/Http';
import logrocket from '@/services/logrocket';

import { EVENT_BUS } from '@/eventBus';
import { globalTZ } from 'lib/utils/date';

export default {
  async APP_INIT_LOAD({ dispatch }) {
    await dispatch('GET_MERCHANT_INFO');

    dispatch('GET_SELF_EMPLOYED_STATUS');
  },

  GET_MERCHANT_INFO({ commit, dispatch }) {
    return HTTPService.getMerchant()
      .then(async response => {
        const { data } = response;
        logrocket.identify(data);

        if (data.timezone) {
          globalTZ.setTimezone(data.timezone.code);
        }
        commit('SET_MERCHANT_INFO', data);
        commit('SET_THEME', data.theme);
        commit('SET_PROJECT_LIST', data.projects);

        EVENT_BUS.$emit('change.localization', data.localization);

        await dispatch('GET_PROJECT_INFO');
      });
  },

  GET_PROJECT_INFO({ commit }) {
    return HTTPService.getProject()
      .then(response => {
        const { data } = response;
        commit('SET_PROJECT_INFO', data);
      });
  },

  async CHANGE_PROJECT({ commit, getters, dispatch }, data) {
    const { MERCHANT } = getters;
    const { projectId, routeName } = data;

    if (MERCHANT.currentProjectId === projectId) {
      return router.push({ name: routeName }).catch(() => {});
    }

    const requestData = {
      id: MERCHANT.id,
      theme: MERCHANT.theme || 'obsidian',
      currentProjectId: projectId,
    };

    await HTTPService.updateMerchant(requestData);

    const projectData = await HTTPService.getProject();
    const project = projectData.data;
    const currency = project.currency;

    if (validate.isNull(currency)) {
      return router.push({ name: 'project.currency' });
    }

    await commit('CLOSE_TRANSACTION');
    await commit('RESET_SUBSCRIPTIONS');

    await dispatch('GET_MERCHANT_INFO');
    await dispatch('GET_GRAPH');
    await dispatch('GET_TRANSACTIONS', {
      page: 1,
      reset: true,
      checkEmpty: true,
    });
    await dispatch('bills/fetchLimits');

    router.push({ name: routeName }).catch(() => {});
  },

  GET_TRANSACTIONS({ state, commit }, params) {
    const { page, reset, checkEmpty } = params;
    const { filterBy, searchText } = state.TRANSACTIONS;

    const transactionsParams = Object.assign(
      { loading: true },
      searchText && { blink: true },
      { checkEmpty: !!checkEmpty }
    );

    commit('SET_TRANSACTIONS_PARAMS', transactionsParams);

    return HTTPService.getTransactions(page, filterBy, searchText)
      .then(response => {
        const { data } = response;

        commit('SET_TRANSACTIONS_PARAMS', {
          ...data,
          resetResult: reset,
          lastDataChunk: data.result,
        });
      })
      .finally(() => {
        commit('SET_TRANSACTIONS_PARAMS', {
          loading: false,
          blink: false,
          searchText: '',
        });
      });
  },

  GET_TRANSACTIONS_AFTER_SCROLLING({ state, dispatch }) {
    if (state.TRANSACTIONS.lastDataChunk.length < state.TRANSACTIONS.countPerPage) {
      return;
    }

    const page = state.TRANSACTIONS.page + 1;
    const reset = false;

    dispatch('GET_TRANSACTIONS', { page, reset });
  },

  GET_GRAPH({ commit }) {
    return HTTPService.getGraph()
      .then(response => {
        commit('SET_GRAPH', response.data);
      });
  },

  async CHANGE_THEME({ commit, getters }) {
    const { MERCHANT } = getters;
    const currentTheme = document.body.className;
    const newTheme = currentTheme === 'obsidian' ? 'eclipse' : 'obsidian';

    const requestData = {
      id: MERCHANT.id,
      theme: newTheme,
      currentProjectId: MERCHANT.currentProjectId,
    };

    try {
      await HTTPService.updateMerchant(requestData);
      commit('SET_THEME', newTheme);
    } catch (error) {
      logrocket.logger(error);
      commit('SET_THEME', currentTheme);
    }
  },

  async GET_SELF_EMPLOYED_STATUS({ commit, dispatch }) {
    commit('SELF_EMPLOYED_LOADING', true);

    await HTTPService.selfEmployedStatus()
      .then(response => {
        commit('SET_SELF_EMPLOYED_STATUS', response.data.status);
        commit('SET_SELF_EMPLOYED_PROPERTIES', response.data.properties);
      })
      .catch(error => {
        logrocket.logger(error);
        commit('SET_SELF_EMPLOYED_STATUS', 'INACTIVE');
      });

    dispatch('FETCH_SELF_EMPLOYED_NOTIFICATIONS');
    commit('SELF_EMPLOYED_LOADING', false);
  },

  GET_SELF_EMPLOYED_TAX_SERVICE_STATUS() {
    return HTTPService.selfEmployedTaxServiceStatus();
  },

  SELF_EMPLOYED_DISCONNECT_REQUEST() {
    return HTTPService.selfEmployedDisconnectRequest();
  },

  // eslint-disable-next-line no-unused-vars
  TRANSACTION_DECLARATION({ commit }, data) {
    return HTTPService.transactionDeclaration(data);
  },

  // eslint-disable-next-line no-unused-vars
  DECLARATION_RECEIPT({ commit }, publicId) {
    return HTTPService.declarationReceipt(publicId);
  },

  GET_RECEIPT_CANCEL_REASONS() {
    return HTTPService.receiptCancelReasons();
  },

  // eslint-disable-next-line no-unused-vars
  SELF_EMPLOYED_RECEIPT_CANCEL({ commit }, data) {
    return HTTPService.receiptCancel(data);
  },

  // eslint-disable-next-line no-unused-vars
  GET_SELF_EMPLOYED_QR_CODE({ commit }, publicId) {
    return HTTPService.selfEmployedQrCode(publicId);
  },

  // eslint-disable-next-line no-unused-vars
  CONNECT_AUTO_DECLARATION({ commit }, data) {
    return HTTPService.connectAutoDeclaration(data);
  },

  // eslint-disable-next-line no-unused-vars
  DISCONNECT_AUTO_DECLARATION({ commit }, projectId) {
    return HTTPService.disconnectAutoDeclaration(projectId);
  },

  async FETCH_SELF_EMPLOYED_NOTIFICATIONS({ getters, commit }) {
    if (!getters.isSelfEmployedActivated) {
      return;
    }

    commit('SET_SELF_EMPLOYED_NOTIFICATION_LOADING', true);

    try {
      const { data: { notifications } } = await HTTPService.fetchSelfEmployedNotifications();

      const notificationsMap = notifications.find(notification => notification.inn === getters.SELF_EMPLOYED_INN);
      const notificationList = validate.isNotUndefined(notificationsMap)
        ? notificationsMap.notif
        : [];

      commit('SET_SELF_EMPLOYED_NOTIFICATIONS', notificationList);
    } catch (error) {
      commit('SET_SELF_EMPLOYED_NOTIFICATIONS', []);
      logrocket.logger(error);
    }

    commit('SET_SELF_EMPLOYED_NOTIFICATION_LOADING', false);
  },

  // eslint-disable-next-line no-unused-vars
  UPDATE_SELF_EMPLOYED_NOTIFICATION({ commit }, notificationId) {
    return HTTPService.updateSelfEmployedNotification(notificationId);
  },

  async GET_SUBSCRIPTIONS({ state, commit }, params) {
    const { page, reset } = params;
    const { filterBy, searchText } = state.SUBSCRIPTIONS;

    const subscriptionsParams = Object.assign(
      { loading: true },
      { checkEmpty: true }
    );

    const filtersMap = {
      'ALL': '',
      'ACTIVE': 'ACTIVE',
      'INACTIVE': 'INACTIVE',
    };

    commit('SET_SUBSCRIPTIONS_PARAMS', subscriptionsParams);

    return HTTPService.getSubscriptions(page, filtersMap[filterBy], searchText)
      .then(response => {
        const { data } = response;

        commit('SET_SUBSCRIPTIONS_PARAMS', {
          ...data,
          resetSubscriber: reset,
          lastDataChunk: data.subscribers,
        });
      })
      .finally(() => {
        commit('SET_SUBSCRIPTIONS_PARAMS', {
          loading: false,
        });
      });
  },

  async GET_SUBSCRIPTIONS_AFTER_SCROLLING({ state, dispatch }) {
    if (state.SUBSCRIPTIONS.lastDataChunk.length < state.SUBSCRIPTIONS.countPerPage) {
      return;
    }

    const page = state.SUBSCRIPTIONS.page + 1;
    const reset = false;

    await dispatch('GET_SUBSCRIPTIONS', { page, reset });
  },

  async FETCH_WRITE_OFF_OPTIONS({ commit }) {
    try {
      const { data } = await HTTPService.fetchWriteOffOptions();
      commit('SET_WRITE_OFF_OPTIONS', data);
    } catch (error) {
      logrocket.logger(error);
      commit('SET_WRITE_OFF_OPTIONS', []);
    }
  },

  async UNFOLLOW_SUBSCRIPTION({ commit }, subscriptionId) {
    try {
      await HTTPService.unfollowSubscription(subscriptionId);
      commit('UPDATE_STATUS_SUBSCRIPTION', subscriptionId);
    } catch (error) {
      logrocket.logger(error);
    }
  },

  async FETCH_MARKETPLACE_CATEGORIES({ commit }) {
    try {
      const { data } = await HTTPService.fetchMarketplaceCategories();
      commit('SET_MARKETPLACE_CATEGORIES', data);
    } catch (error) {
      logrocket.logger(error);
      commit('SET_MARKETPLACE_CATEGORIES', []);
    }
  },

  async GET_LOCALE_BY_PARAMS({ commit }, params) {
    console.log('GET_LOCALE_BY_PARAMS', params);

    try {
      const { data } = await HTTPService.getProjectLocale(params);
      commit('SET_LOCALE', data);

      return data;
    } catch (error) {
      console.log(error);
    }
  },
};
